import getConfig from 'next/config';
import {
  getAuth,
  inMemoryPersistence,
  initializeAuth,
  setPersistence,
} from '@firebase/auth';
import { Firestore, getFirestore } from '@firebase/firestore';
import { FirebaseApp, getApp, initializeApp } from '@firebase/app';
import { isInWebView } from '@utils/mobile';

class FirebaseService {
  private database: Firestore | undefined;
  public app: FirebaseApp | undefined;

  constructor(firebaseConfig: any) {
    try {
      this.app = getApp();
    } catch (error) {
      if (typeof window !== 'undefined') {
        this.app = initializeApp(firebaseConfig);
      }
    }

    if (this.app) {
      // In the native app WebView, Firebase Auth's default IndexedDB persistence
      // is harmful: several same-origin WebViews share one IndexedDB and, unlike
      // browser tabs, don't coordinate access — a concurrent open throws
      // "InvalidStateError: the database connection is closing", aborting the
      // auth bootstrap and leaving the page blank. The app re-injects a custom
      // token on every WebView load, so cross-navigation persistence is unneeded
      // here. Browsers are unaffected and keep the default persistence.
      if (isInWebView()) {
        try {
          initializeAuth(this.app, { persistence: inMemoryPersistence });
        } catch {
          // Auth already initialized (import ordering): switch it anyway.
          void setPersistence(getAuth(this.app), inMemoryPersistence).catch(
            () => undefined,
          );
        }
      }

      this.database = getFirestore(this.app);
    }
  }

  public get db() {
    if (!this.database) {
      throw new Error('Firebase database not initialized');
    }

    return this.database;
  }
}

const {
  publicRuntimeConfig: { firebaseConfig },
} = getConfig();

export default new FirebaseService(firebaseConfig);
